import { useState } from 'react';
import { supabase } from '../lib/supabase';

export function useActivityResults() {
  const [loading, setLoading] = useState(false);

  const getActivityResults = async (activityId: string, childId?: string | null) => {
    setLoading(true);
    try {
      let query = supabase
        .from('child_progress')
        .select(`
          *,
          children (
            name,
            avatar_color
          )
        `)
        .eq('activity_id', activityId)
        .order('completed_at', { ascending: false });

      // If a specific child is selected, filter by that child
      if (childId) {
        query = query.eq('child_id', childId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching activity results:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getChildActivityResults = async (childId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_progress')
        .select(`
          *,
          activities (
            title,
            subject,
            difficulty_level
          )
        `)
        .eq('child_id', childId)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching child activity results:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getActivityStatistics = async (activityId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_progress')
        .select('score, time_spent, points_earned')
        .eq('activity_id', activityId);

      if (error) throw error;

      const results = data || [];
      const totalAttempts = results.length;
      const averageScore = totalAttempts > 0 
        ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / totalAttempts)
        : 0;
      const totalTimeSpent = results.reduce((sum, r) => sum + r.time_spent, 0);
      const totalPointsEarned = results.reduce((sum, r) => sum + r.points_earned, 0);

      return {
        totalAttempts,
        averageScore,
        totalTimeSpent,
        totalPointsEarned,
        highestScore: totalAttempts > 0 ? Math.max(...results.map(r => r.score)) : 0,
        lowestScore: totalAttempts > 0 ? Math.min(...results.map(r => r.score)) : 0,
      };
    } catch (error) {
      console.error('Error fetching activity statistics:', error);
      return {
        totalAttempts: 0,
        averageScore: 0,
        totalTimeSpent: 0,
        totalPointsEarned: 0,
        highestScore: 0,
        lowestScore: 0,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    getActivityResults,
    getChildActivityResults,
    getActivityStatistics,
  };
}